import React, { useState, useRef } from 'react';
import { useLang } from '../context/LanguageContext';
import FormShell, { Field, inputCls } from './FormShell';
import { submitToSheet, generateSubmissionId, getVisitorId, sanitizePhone } from '../lib/api';

const GetStartedModal = ({ open, onClose, pageSource = 'services' }) => {
  const { t, lang } = useLang();
  const initial = {
    full_name: '',
    email: '',
    phone: '',
    country: '',
    primary_requirement: '',
    company_name: '',
    industry: '',
    business_stage: '',
    timeline: '',
    budget_currency: '',
    estimated_budget_amount: '',
    budget_not_sure: false,
    specific_details: '',
    preferred_contact_method: '',
    consent: false
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refNo, setRefNo] = useState('');
  const submitLock = useRef(false);

  if (!open) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setLocked(true);
    const submission_id = generateSubmissionId('SIB-GS');
    const payload = {
      form_type: 'get_started',
      submission_id,
      visitor_id: getVisitorId(),
      lead_source: 'website',
      primary_requirement: form.primary_requirement,
      full_name: form.full_name,
      email: form.email,
      phone: form.phone,
      country: form.country,
      nationality: '',
      preferred_language: lang,
      preferred_contact_method: form.preferred_contact_method,
      applicant_type: '',
      company_name: form.company_name,
      company_website: '',
      linkedin: '',
      industry: form.industry,
      business_stage: form.business_stage,
      timeline: form.timeline,
      preferred_saudi_locations: '',
      budget_currency: form.budget_currency,
      estimated_budget_amount: form.estimated_budget_amount,
      budget_not_sure: form.budget_not_sure ? 'yes' : 'no',
      specific_details: form.specific_details,
      objective: '',
      main_challenge: '',
      additional_notes: '',
      preferred_contact_time: '',
      referral_source: '',
      language: lang,
      page_source: pageSource,
      status: 'submitted'
    };
    await submitToSheet(payload);
    setRefNo(submission_id);
    setSubmitting(false);
    setSuccess(true);
    // Keep locked for 4s after success so re-open cannot re-submit rapidly
    setTimeout(() => { submitLock.current = false; setLocked(false); }, 4000);
  };

  const handleReset = () => { setSuccess(false); setRefNo(''); setForm(initial); submitLock.current = false; setLocked(false); };

  const currencies = ['USD', 'SAR', 'EUR', 'GBP', 'AED', 'CNY'];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}>
        <FormShell
          title={t.forms.getStartedTitle}
          desc={t.forms.getStartedDesc}
          onSubmit={handleSubmit}
          submitting={submitting}
          locked={locked}
          success={success}
          onReset={handleReset}
          close={onClose}
          refNo={refNo}
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t.forms.fullName} required>
              <input required className={inputCls} value={form.full_name} onChange={e => set('full_name', e.target.value)} />
            </Field>
            <Field label={t.forms.email} required>
              <input required type="email" className={inputCls} dir="ltr" value={form.email} onChange={e => set('email', e.target.value)} />
            </Field>
            <Field label={t.forms.phone} required>
              <input
                required
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                pattern="[0-9+\s\-()]{6,20}"
                title={t.forms.phoneHelp}
                className={inputCls}
                dir="ltr"
                value={form.phone}
                onChange={e => set('phone', sanitizePhone(e.target.value))}
                onKeyDown={(e) => {
                  const allowed = /[0-9+\s\-()]/;
                  if (e.key.length === 1 && !allowed.test(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault();
                }}
              />
              <p className="text-[11px] text-[#33473d]/60 mt-1">{t.forms.phoneHelp}</p>
            </Field>
            <Field label={t.forms.country} required>
              <select required className={inputCls} value={form.country} onChange={e => set('country', e.target.value)}>
                <option value="">{t.forms.selectCountry}</option>
                {t.forms.countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t.forms.interestedIn} required>
            <select required className={inputCls} value={form.primary_requirement} onChange={e => set('primary_requirement', e.target.value)}>
              <option value="">{t.forms.selectCountry}</option>
              {t.forms.interestOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t.forms.industry}>
              <select className={inputCls} value={form.industry} onChange={e => set('industry', e.target.value)}>
                <option value="">{t.forms.selectCountry}</option>
                {t.forms.industryOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.forms.businessStage}>
              <select className={inputCls} value={form.business_stage} onChange={e => set('business_stage', e.target.value)}>
                <option value="">{t.forms.selectCountry}</option>
                {t.forms.businessStageOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.forms.timeline}>
              <select className={inputCls} value={form.timeline} onChange={e => set('timeline', e.target.value)}>
                <option value="">{t.forms.selectCountry}</option>
                {t.forms.timelineOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.forms.preferredContact}>
              <select className={inputCls} value={form.preferred_contact_method} onChange={e => set('preferred_contact_method', e.target.value)}>
                <option value="">{t.forms.selectCountry}</option>
                <option value="Phone Call">{t.forms.phoneCall}</option>
                <option value="WhatsApp Call">{t.forms.whatsappCall}</option>
                <option value="WhatsApp Message">{t.forms.whatsappMsg}</option>
                <option value="Email">{t.forms.contactMethodEmail}</option>
              </select>
            </Field>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label={t.forms.budgetCurrency}>
              <select className={inputCls} value={form.budget_currency} onChange={e => set('budget_currency', e.target.value)} disabled={form.budget_not_sure}>
                <option value="">{t.forms.selectCountry}</option>
                {currencies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.forms.budgetAmount}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9,]*"
                className={inputCls}
                dir="ltr"
                value={form.estimated_budget_amount}
                onChange={e => set('estimated_budget_amount', e.target.value.replace(/[^0-9,]/g, ''))}
                disabled={form.budget_not_sure}
              />
            </Field>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-[#33473d]">
                <input type="checkbox" checked={form.budget_not_sure} onChange={e => set('budget_not_sure', e.target.checked)} />
                <span>{t.forms.budgetNotSure}</span>
              </label>
            </div>
          </div>
          <Field label={t.forms.companyName}>
            <input className={inputCls} value={form.company_name} onChange={e => set('company_name', e.target.value)} />
          </Field>
          <Field label={t.forms.message}>
            <textarea className={inputCls} rows={3} value={form.specific_details} onChange={e => set('specific_details', e.target.value)} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-[#33473d]">
            <input type="checkbox" required checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-1" />
            <span>{t.forms.consent}</span>
          </label>
        </FormShell>
      </div>
    </div>
  );
};

export default GetStartedModal;
